import {
  I2cRadioButton,
  I2cCheckbox,
  I2cCheckboxGroup,
  I2cRadioGroup,
  I2cRadio,
  I2cListItem,
  I2cInput,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../../PrepaidComponents/Prepaid.scss";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup"; 
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import { validationSchemaNativeMobileApplication } from "./ValidationSchema";
import { agentWebsiteLang, webRequire, nativeAppPlatform, appPublish } from "./stringUtils";
import { handleCheckboxArrayToggle, handleRadioChange } from "./formUtils";
 
const NativeMobileApplication = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
}) => {
  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchemaNativeMobileApplication),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      native_mobile_application_would_you_like_mobile_app: "no",
      native_mobile_application_native_platform: [],
      native_mobile_application_mobile_language: [],
      native_mobile_application_mobile_language_other: "",
      native_mobile_application_mobile_app_publishing: "",
    },
  });

  const {
    formState: { errors },
  } = methods;

  const fields = [
    "native_mobile_application_would_you_like_mobile_app",
    "native_mobile_application_native_platform",
    "native_mobile_application_mobile_language",
    "native_mobile_application_mobile_language_other",
    "native_mobile_application_mobile_app_publishing",
  ];

  // Handle value changes and update parent state
  const handleValueChanges = React.useCallback(() => {
    const values = methods.getValues();
    setAddFormData({
      ...addFormData,
      3: {
        ...addFormData["3"],
        "3_4": { ...values },
      },
    });
  }, [methods, setAddFormData, addFormData]);

  // Load existing form data
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        const value = formDataJson[3]?.["3_4"]?.[field];
        if (value !== undefined && value !== null) {
          methods.setValue(field, value);
        }
      });
      methods.trigger();
      handleValueChanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  // Trigger validation when component mounts
  useEffect(() => {
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update form validation state when errors change
  useEffect(() => {
    const errorsLength = Object.keys(errors)?.length;
    if (errorsLength === 0) {
      setFormValidStates("3_4", true);
    } else {
      setFormValidStates("3_4", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const isWebRequired = methods.watch("native_mobile_application_would_you_like_mobile_app") === "yes";

  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-md">
          <div i2c-grid="12">
            <div className="section-space">
              <div className="sub-heading mb-16">
                Would you like Mobile Application?
              </div>
              <I2cRadioGroup
                label=""
                value={methods.watch("native_mobile_application_would_you_like_mobile_app")}
                name="native_mobile_application_would_you_like_mobile_app"
                onI2cChange={(e) => {
                  const value = String(e.target.value);
                  
                  handleRadioChange(
                    methods,
                    "native_mobile_application_would_you_like_mobile_app",
                    value,
                    value !== "yes" ? {
                      native_mobile_application_native_platform: [],
                      native_mobile_application_mobile_language: [],
                      native_mobile_application_mobile_language_other: "",
                      native_mobile_application_mobile_app_publishing: "",
                    } : {},
                    handleValueChanges
                  );
                }}
              >
                {webRequire.map((option) => (
                  <I2cRadioButton key={option.value} value={option.value}>
                    {option.label}
                  </I2cRadioButton>
                ))}
              </I2cRadioGroup>
              {errors.native_mobile_application_would_you_like_mobile_app && (
                <ErrorMessage message={errors.native_mobile_application_would_you_like_mobile_app.message} />
              )}
            </div>
            {isWebRequired && (
              <>
                <div className="section-space">
                  <div className="sub-heading mb-16">Native Platform</div>
                  <I2cCheckboxGroup key={`native-platform-${methods.watch("native_mobile_application_would_you_like_mobile_app")}`}>
                    <div i2c-grid="grid gap-sm">
                      {nativeAppPlatform.map((web, index) => (
                        <I2cListItem
                          key={index}
                          i2c-grid="4@md"
                          variant="receipt"
                          label={web.label}
                          value=""
                          type="checkbox"
                          checked={methods.watch("native_mobile_application_native_platform").includes(web.value)}
                          onI2cCheckboxClicked={() =>
                            handleCheckboxArrayToggle(
                              methods,
                              "native_mobile_application_native_platform",
                              web.value,
                              null, // No dependent field for platform
                              handleValueChanges
                            )
                          }
                        >
                          <I2cCheckbox
                            slot="prefix"
                            name="ic_cAgentLang"
                            value={web.value}
                            label=""
                            filled
                            checked={methods.watch("native_mobile_application_native_platform").includes(web.value)}
                            className="customCheckbox"
                          ></I2cCheckbox>
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cCheckboxGroup>
                  {errors.native_mobile_application_native_platform && (
                    <ErrorMessage message={errors.native_mobile_application_native_platform.message} />
                  )}
                </div>
                <div className="section-space">
                  <div className="sub-heading mb-16">C-Holder Mobile Language</div>
                  <I2cCheckboxGroup key={`native-language-${methods.watch("native_mobile_application_would_you_like_mobile_app")}`}>
                    <div i2c-grid="grid gap-sm">
                      {agentWebsiteLang.map((web, index) => (
                        <I2cListItem
                          key={index}
                          i2c-grid="4@md"
                          variant="receipt"
                          label={web.label}
                          value=""
                          type="checkbox"
                          checked={methods.watch("native_mobile_application_mobile_language").includes(web.value)}
                          onI2cCheckboxClicked={() =>
                            handleCheckboxArrayToggle(
                              methods,
                              "native_mobile_application_mobile_language",
                              web.value,
                              { dependentField: "native_mobile_application_mobile_language_other" }, // Clear "other" field when "other" is unchecked
                              handleValueChanges
                            )
                          }
                        >
                          <I2cCheckbox
                            slot="prefix"
                            name="ic_cAgentLang"
                            value={web.value}
                            label=""
                            filled
                            checked={methods.watch("native_mobile_application_mobile_language").includes(web.value)}
                            className="customCheckbox"
                          ></I2cCheckbox>
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cCheckboxGroup>
                  {errors.native_mobile_application_mobile_language && (
                    <ErrorMessage message={errors.native_mobile_application_mobile_language.message} />
                  )}
                  {methods
                    .watch("native_mobile_application_mobile_language")
                    .includes("other") && (
                    <div i2c-grid="grid gap-sm" className="mt-16">
                      <div i2c-grid="4@md">
                        <div className="single-field-wrapper">
                          <I2cInput
                            className="single-input-field"
                            placeholder="Please Specify"
                            size="large"
                            label="Enter Details"
                            name="native_mobile_application_mobile_language_other"
                            value={methods.watch(
                              "native_mobile_application_mobile_language_other",
                            )}
                            onI2cInput={(e) => {
                              const value = e.target.value;
                              methods.setValue(
                                "native_mobile_application_mobile_language_other",
                                value,
                              );
                              methods.trigger();
                              handleValueChanges();
                            }}
                            invalid={
                              errors.native_mobile_application_mobile_language_other
                                ? true
                                : false
                            }
                          />
                          {errors.native_mobile_application_mobile_language_other && (
                            <ErrorMessage
                              message={
                                errors
                                  .native_mobile_application_mobile_language_other
                                  .message
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="section-space">
                  <div className="sub-heading">Mobile App Publishing</div>
                  <I2cRadioGroup
                    value={methods.watch("native_mobile_application_mobile_app_publishing")}
                    onI2cChange={(e) => {
                      const value = e.target?.value || e;
                      methods.setValue("native_mobile_application_mobile_app_publishing", value);
                      methods.trigger();
                      handleValueChanges();
                    }}
                    label="How will the mobile application be published:"
                  >
                    <div i2c-grid="grid gap-sm">
                      {appPublish.map((App, index) => (
                        <I2cListItem
                          key={index}
                          i2c-grid="4@md"
                          variant="receipt"
                          label={App.label}
                          sublabel=""
                          value=""
                          type="checkbox"
                        >
                          <I2cRadio
                            slot="prefix"
                            filled
                            name="channel_mobAppPublish"
                            value={App.value}
                          >
                            {" "}
                          </I2cRadio>
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cRadioGroup>
                  {errors.native_mobile_application_mobile_app_publishing && (
                    <ErrorMessage message={errors.native_mobile_application_mobile_app_publishing.message} />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default NativeMobileApplication;
